import React, { useEffect, useState } from 'react';
import api from '@/lib/api';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Bed, AlertCircle, RefreshCw, ChevronRight, Sparkles } from 'lucide-react';
import { PilotFeedbackWidget } from '@/components/pilot/PilotFeedbackWidget';
import { useConfirm } from '@/hooks/useAppDialog';

/**
 * SleepoverFinanceHealthTile
 *
 * Dashboard KPI tile showing:
 *   - Mismatch shifts (requires finance attention)
 *   - Pending adjustments count + net value
 *   - Oldest unreconciled shift age
 *   - "Roll up now" quick action when there are pending adjustments
 *
 * Feature-flag tolerant — 403 ⇒ render nothing.
 */
export function SleepoverFinanceHealthTile({ className = '' }) {
  const confirm = useConfirm();
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rolling, setRolling] = useState(false);
  const [enabled, setEnabled] = useState(true);
  const [lastRefreshedAt, setLastRefreshedAt] = useState(null);
  const [autoRefreshOn, setAutoRefreshOn] = useState(false);
  const [, setTick] = useState(0);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get('/admin/sleepover/finance-health?days=30');
      setHealth(r.data?.data);
      setEnabled(true);
      setLastRefreshedAt(Date.now());
    } catch (e) {
      if (e.response?.status === 403) {
        setEnabled(false);
      }
    } finally { setLoading(false); }
  };

  useEffect(() => {
    load();
    // Read auto-refresh flag once on mount (cheap, cached server-side)
    api.get('/production/feature-flags/mine')
      .then((r) => {
        const flags = r.data?.data || r.data || {};
        setAutoRefreshOn(!!flags.sleepover_finance_auto_refresh_enabled);
      })
      .catch(() => {});
  }, []);

  // Keep the "last refreshed" label live without re-fetching data
  useEffect(() => {
    if (!lastRefreshedAt) return;
    const iv = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(iv);
  }, [lastRefreshedAt]);

  // Optional auto-refresh every 60s when feature flag is ON
  useEffect(() => {
    if (!autoRefreshOn) return;
    const iv = setInterval(() => { load(); }, 60000);
    return () => clearInterval(iv);
  }, [autoRefreshOn]);

  const formatAgo = (ts) => {
    if (!ts) return '';
    const s = Math.max(0, Math.floor((Date.now() - ts) / 1000));
    if (s < 5) return 'just now';
    if (s < 60) return `${s}s ago`;
    const m = Math.floor(s / 60);
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    return `${h}h ago`;
  };

  const handleRollup = async () => {
    const ok = await confirm({
      title: 'Roll up pending adjustments?',
      description: 'Bundle all pending sleep-over adjustments into a DRAFT credit claim batch. The batch can be reviewed and edited before submission.',
      confirmLabel: 'Roll up now',
      variant: 'info',
      testId: 'sleepover-rollup-confirm',
    });
    if (!ok) return;
    setRolling(true);
    try {
      const r = await api.post('/admin/sleepover/adjustments/rollup', { max_age_days: 90, note: 'Admin rollup from dashboard' });
      const s = r.data?.summary || {};
      toast.success(
        `Rolled up ${s.adjustment_count || 0} adjustment(s) · ${s.line_count || 0} lines · net $${(s.net_total || 0).toFixed(2)}`,
        { duration: 6000 },
      );
      load();
    } catch (e) {
      const data = e.response?.data || {};
      toast.error(data.error?.message || data.detail?.message || data.detail || 'Rollup failed');
    } finally { setRolling(false); }
  };

  if (!enabled) return null;

  const h = health || {};
  const hasMismatch = (h.mismatch_shifts || 0) > 0;
  const hasPending = (h.pending_adjustments_count || 0) > 0;
  const tone =
    hasMismatch ? 'border-red-300 bg-red-50/40' :
    hasPending ? 'border-amber-300 bg-amber-50/40' :
    'border-green-300 bg-green-50/40';

  return (
    <Card className={`${tone} ${className}`} data-testid="sleepover-finance-health-tile">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2">
          <Bed className="w-4 h-4" /> Sleep-Over Finance Health
          <Badge variant="outline" className="text-[9px] ml-auto">last {h.range_days || 30}d</Badge>
          {lastRefreshedAt && !loading && (
            <span
              className="text-[10px] text-muted-foreground tabular-nums"
              title={new Date(lastRefreshedAt).toLocaleString()}
              data-testid="sleepover-finance-last-refreshed"
            >
              · refreshed {formatAgo(lastRefreshedAt)}{autoRefreshOn ? ' · auto' : ''}
            </span>
          )}
          <button
            type="button"
            onClick={load}
            disabled={loading}
            className="ml-1 text-muted-foreground hover:text-foreground disabled:opacity-50"
            title="Refresh now"
            data-testid="sleepover-finance-refresh-btn"
          >
            <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2.5 text-sm">
        {loading ? (
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <RefreshCw className="w-3 h-3 animate-spin" /> Loading health…
          </div>
        ) : (
          <>
            <div className="grid grid-cols-3 gap-2 text-center">
              <div className="bg-white rounded-md p-2 border" data-testid="kpi-mismatch">
                <p className="text-[10px] text-muted-foreground uppercase tracking-wide">Mismatch</p>
                <p className={`text-2xl font-bold ${hasMismatch ? 'text-red-700' : 'text-slate-700'}`}>
                  {h.mismatch_shifts || 0}
                </p>
                <p className="text-[10px] text-muted-foreground">
                  ${(h.mismatch_total_aud || 0).toFixed(2)}
                </p>
              </div>
              <div className="bg-white rounded-md p-2 border" data-testid="kpi-pending">
                <p className="text-[10px] text-muted-foreground uppercase tracking-wide">Pending adj.</p>
                <p className={`text-2xl font-bold ${hasPending ? 'text-amber-700' : 'text-slate-700'}`}>
                  {h.pending_adjustments_count || 0}
                </p>
                <p className="text-[10px] text-muted-foreground">
                  net ${(h.pending_adjustments_net_aud || 0).toFixed(2)}
                </p>
              </div>
              <div className="bg-white rounded-md p-2 border" data-testid="kpi-oldest">
                <p className="text-[10px] text-muted-foreground uppercase tracking-wide">Oldest Δ</p>
                <p className={`text-2xl font-bold ${(h.oldest_unreconciled_days || 0) > 14 ? 'text-red-700' : 'text-slate-700'}`}>
                  {h.oldest_unreconciled_days || 0}d
                </p>
                <p className="text-[10px] text-muted-foreground">
                  {h.shifts_invoiced || 0}/{h.sleepover_shifts_total || 0} invoiced
                </p>
              </div>
            </div>

            {hasPending && (
              <div className="flex items-center gap-2 pt-1">
                <Button
                  size="sm"
                  onClick={handleRollup}
                  disabled={rolling}
                  className="flex-1 h-7 text-xs"
                  data-testid="sleepover-rollup-btn"
                >
                  <Sparkles className="w-3 h-3 mr-1" />
                  {rolling ? 'Rolling up…' : 'Roll up pending to DRAFT credit batch'}
                </Button>
              </div>
            )}

            <div className="flex items-center justify-between pt-1 border-t">
              <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
                {hasMismatch ? (
                  <><AlertCircle className="w-3 h-3 text-red-600" /> Finance action required</>
                ) : hasPending ? (
                  <><AlertCircle className="w-3 h-3 text-amber-600" /> Pending adjustments</>
                ) : (
                  <>All reconciled ✓</>
                )}
              </div>
              <Link to="/admin/sleepover/comparison" data-testid="sleepover-comparison-link">
                <Button size="sm" variant="ghost" className="h-6 text-[11px]">
                  Open report <ChevronRight className="w-3 h-3 ml-0.5" />
                </Button>
              </Link>
            </div>
            <div className="pt-1">
              <PilotFeedbackWidget
                feature="sleepover-finance-tile"
                compact
              />
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
